import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { loadModules } from 'esri-loader';
import { Box, CircularProgress, Typography } from '@mui/material';
import markerIcon from '../../CustomVestmapViewer/assets/marker center.png';
import { normalizeLayersLegendYear } from '../../../utils/legendYear';

const DEFAULT_SECTIONS = [
  { key: 'demographics', label: 'Demographics', webmapID: 'eec677cb49704c48b79c5e8af2214409' },
  { key: 'income', label: 'Income', webmapID: '716041d8fa6345b6b59a24f38ad9f54b' },
  { key: 'crime', label: 'Crime', webmapID: 'b43167d1c89140a1b9e0d1566da3d8f1' },
  { key: 'expansion', label: 'Expansion', webmapID: '8b1cb62cfb8e43c3874016694043f024' },
  { key: 'schools', label: 'Schools', webmapID: '655b897aece849c19770e7637a018bd8' },
  { key: 'hpi', label: 'House Price Index', webmapID: 'fff2d74039b84c16836bd311b66beeac' },
  { key: 'neighborhood', label: 'Neighborhood', webmapID: 'a51ea46750874e768e6c49b06a717642' },
];

export default function SharedMap() {
  const { shareId } = useParams();
  const mapRef = useRef(null);
  const viewRef = useRef(null);

  const [mapConfig, setMapConfig] = useState(null);
  const [esriToken, setEsriToken] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch map config and token on mount
  useEffect(() => {
    async function fetchData() {
      try {
        const [configRes, tokenRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_NODE_URL}/maps/${shareId}`),
          fetch(`${process.env.REACT_APP_NODE_URL}/maps/token`),
        ]);

        if (!configRes.ok) {
          const data = await configRes.json().catch(() => ({}));
          throw new Error(data.message || 'Share link not found');
        }

        const configData = await configRes.json();
        setMapConfig(configData);

        if (tokenRes.ok) {
          const tokenData = await tokenRes.json();
          setEsriToken(tokenData.token);
        }

        // Determine initial section
        const sections = configData.mapConfig?.sections || DEFAULT_SECTIONS;
        const initialWebmapID = configData.mapConfig?.webmapID;
        const initialSection = initialWebmapID
          ? sections.find(s => s.webmapID === initialWebmapID) || sections[0]
          : sections[0];
        setActiveSection(initialSection);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [shareId]);

  // Render map when section or token changes
  useEffect(() => {
    if (!activeSection || !mapRef.current) return;

    const token = esriToken || mapConfig?.mapConfig?.esriToken;
    if (!token) return;

    let cancelled = false;

    async function renderMap() {
      setMapLoading(true);

      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }

      const [WebMap, MapView, Graphic, IdentityManager, Legend] =
        await loadModules([
          'esri/WebMap',
          'esri/views/MapView',
          'esri/Graphic',
          'esri/identity/IdentityManager',
          'esri/widgets/Legend',
        ]);

      if (cancelled) return;

      IdentityManager.registerToken({
        server: 'https://www.arcgis.com/sharing/rest',
        token,
      });
      IdentityManager.registerToken({
        server: 'https://utility.arcgis.com',
        token,
      });

      const lat = mapConfig.latitude || mapConfig.mapConfig?.latitude || 39.0997;
      const lng = mapConfig.longitude || mapConfig.mapConfig?.longitude || -94.5786;

      const webmap = new WebMap({
        portalItem: { id: activeSection.webmapID },
      });

      // Normalize legend year labels (2021-2023 -> 2024) to match the data
      // tables. The shared-map legends come straight from the authored ArcGIS
      // Online WebMap layer titles / field aliases, so we rewrite them
      // client-side here.
      //
      // These webmaps contain stale sibling layers (e.g. "Income 2021/2022")
      // whose portal items are gone, so webmap.loadAll() REJECTS and would skip
      // normalization entirely. Instead load the webmap definition, best-effort
      // load each layer (ignoring failures via allSettled), then normalize the
      // refreshed layer tree (group children included).
      const loadAllLayersBestEffort = () =>
        Promise.allSettled(
          webmap.allLayers
            .toArray()
            .map(layer =>
              layer.load ? layer.load().catch(() => {}) : Promise.resolve(),
            ),
        );
      try {
        await webmap.load();
        if (cancelled) return;
        // First pass loads top-level layers (incl. groups); loading a group
        // surfaces its children into allLayers, so a second pass loads those
        // children (and their fields) before we normalize.
        await loadAllLayersBestEffort();
        if (cancelled) return;
        await loadAllLayersBestEffort();
        if (cancelled) return;
        normalizeLayersLegendYear(webmap.allLayers);
      } catch (err) {
        console.warn('Could not normalize legend year labels:', err);
      }

      const view = new MapView({
        container: mapRef.current,
        map: webmap,
        center: [lng, lat],
        zoom: 13,
        ui: { components: ['zoom', 'compass'] },
        constraints: { minZoom: 4, maxZoom: 19 },
      });

      view.graphics.add(
        new Graphic({
          geometry: { type: 'point', longitude: lng, latitude: lat },
          symbol: {
            type: 'picture-marker',
            url: markerIcon,
            width: '50px',
            height: '100px',
          },
        }),
      );

      // Add responsive legend
      const legendStyle = document.createElement('style');
      legendStyle.textContent = `
        .esri-legend {
          max-height: 150px !important;
          max-width: 150px !important;
          overflow-y: auto !important;
          overflow-x: auto !important;
          background-color: rgba(255, 255, 255, 0.9) !important;
        }
        @media screen and (min-width: 600px) {
          .esri-legend {
            max-height: 300px !important;
            max-width: 250px !important;
          }
        }
        .esri-legend__service {
          padding: 8px !important;
        }
      `;
      document.head.appendChild(legendStyle);

      view.ui.add(new Legend({ view }), 'top-right');

      view
        .when(() => {
          if (!cancelled) {
            viewRef.current = view;
            setMapLoading(false);
          }
        })
        .catch(err => {
          console.error('Map failed to load:', err);
          if (!cancelled) setMapLoading(false);
        });
    }

    renderMap();

    return () => {
      cancelled = true;
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
    };
  }, [activeSection, esriToken, mapConfig]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#1a1a2e',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: '#4FA490' }} />
        <Typography sx={{ color: '#94a3b8' }}>Loading shared map...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#1a1a2e',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography sx={{ color: '#e57373', fontSize: '1.2rem', fontWeight: 600 }}>
          {error}
        </Typography>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
          This link may have expired or been removed.
        </Typography>
      </Box>
    );
  }

  const address =
    mapConfig?.address || mapConfig?.mapConfig?.address || '';
  const sections = mapConfig?.mapConfig?.sections || DEFAULT_SECTIONS;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        background: '#1a1a2e',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.2,
          background: '#0f172a',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{ fontSize: '14px', fontWeight: 600, color: '#4FA490', whiteSpace: 'nowrap' }}
        >
          {activeSection?.label || 'VestMap'}
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            color: '#94a3b8',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {address}
        </Typography>
      </Box>

      {/* Section Tabs — hidden when single section */}
      {sections.length > 1 && (
      <Box
        sx={{
          display: 'flex',
          gap: '4px',
          px: 2,
          py: 1,
          background: '#0f172a',
          overflowX: 'auto',
          flexShrink: 0,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: 2 },
        }}
      >
        {sections.map(section => (
          <Box
            key={section.key}
            component='button'
            onClick={() => setActiveSection(section)}
            sx={{
              px: 1.5,
              py: 0.75,
              border: 'none',
              borderRadius: '6px',
              background:
                activeSection?.key === section.key
                  ? 'rgba(79,164,144,0.2)'
                  : 'rgba(255,255,255,0.06)',
              color: activeSection?.key === section.key ? '#4FA490' : '#94a3b8',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s',
              '&:hover': {
                background: 'rgba(255,255,255,0.1)',
                color: '#e2e8f0',
              },
            }}
          >
            {section.label}
          </Box>
        ))}
      </Box>
      )}

      {/* Map Container */}
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        {mapLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#1a1a2e',
              zIndex: 10,
              color: '#94a3b8',
              fontSize: '14px',
            }}
          >
            <CircularProgress size={24} sx={{ color: '#4FA490', mr: 1.5 }} />
            Loading map...
          </Box>
        )}
        <Box
          ref={mapRef}
          sx={{
            width: '100%',
            height: '100%',
          }}
        />
      </Box>
    </Box>
  );
}
